import { useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { initials } from '@/shared/lib/format';
import nexopsLogo from '@/assets/nexops-logo-dark.svg';
import lilufreshLogo from '@/assets/lilufresh-logo-dark.png';
import { useBranding } from './useBranding';

/** Logo klienta: z organizacji, a dla LiluFresh — wbudowane w aplikację. */
export function useClientLogoSrc(): string | null {
  const { data } = useBranding();
  return data?.logoUrl ?? (data && /lilufresh/i.test(data.name) ? lilufreshLogo : null);
}

/** NEXOPS product mark (geometric "N" tile). */
export function NexopsMark({ className, onDark }: { className?: string; onDark?: boolean }) {
  return (
    <svg viewBox="0 0 32 32" className={cn('size-7 shrink-0', className)} aria-hidden>
      <rect width="32" height="32" rx="8" className={onDark ? 'fill-white' : 'fill-foreground'} />
      <path d="M9 23V9h3.2l7.6 9.4V9H23v14h-3.2l-7.6-9.4V23z" className={onDark ? 'fill-[#0f172a]' : 'fill-background'} />
      <circle cx="25.5" cy="6.5" r="3" className="fill-primary" />
    </svg>
  );
}

/** "NEXOPS" wordmark (mark + letters). */
export function NexopsWordmark({ collapsed, className, onDark }: { collapsed?: boolean; className?: string; onDark?: boolean }) {
  // Na ciemnym tle (sidebar) — wersja logo z białym napisem.
  if (onDark && !collapsed) {
    return (
      <span className={cn('flex w-full items-center justify-center px-1', className)}>
        <img src={nexopsLogo} alt="NEXOPS" className="h-7 w-auto" draggable={false} />
      </span>
    );
  }
  return (
    <span className={cn('flex items-center gap-2', className)}>
      <NexopsMark onDark={onDark} />
      {!collapsed && <span className={cn('text-[15px] font-extrabold tracking-[0.14em]', onDark ? 'text-white' : 'text-foreground')}>NEXOPS</span>}
    </span>
  );
}

/** Client (tenant) logo, or a neat initials placeholder tinted with the brand colour. */
export function ClientLogo({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const { data } = useBranding();
  const logoSrc = data?.logoUrl ?? null;
  const [broken, setBroken] = useState(false);
  const name = data?.name ?? '';
  const dim = size === 'lg' ? 'size-10 text-sm' : size === 'md' ? 'size-7 text-[11px]' : 'size-5 text-[9px]';

  if (logoSrc && !broken) {
    return (
      <img
        src={logoSrc}
        alt={name}
        onError={() => setBroken(true)}
        className={cn('shrink-0 rounded-md bg-white object-contain p-0.5 ring-1 ring-border', dim, className)}
      />
    );
  }
  const letters = initials(...name.split(/\s+/).slice(0, 2)) || '··';
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md bg-secondary font-bold text-secondary-foreground ring-1 ring-white/10',
        dim,
        className,
      )}
    >
      {letters}
    </span>
  );
}

/** Client chip for the sidebar header: logo + name. */
export function ClientBadge({ collapsed }: { collapsed?: boolean }) {
  const { data, isLoading } = useBranding();
  const clientLogo = useClientLogoSrc();
  if (isLoading) return <span className="h-9 rounded-md bg-muted" />;
  if (!data) return null;
  if (!collapsed && clientLogo) {
    return (
      <div className="flex w-full items-center justify-center px-1" title={data.name}>
        <img src={clientLogo} alt={data.name} className="h-7 w-auto" draggable={false} />
      </div>
    );
  }
  return (
    <div
      className={cn('flex min-w-0 items-center gap-2 rounded-md border border-sidebar-border bg-card/60 px-2 py-1.5', collapsed && 'justify-center border-transparent bg-transparent px-0')}
      title={data.name}
    >
      <ClientLogo size={collapsed ? 'md' : 'sm'} />
      {!collapsed && <span className="truncate text-xs font-medium text-foreground">{data.name}</span>}
    </div>
  );
}
